/**
 * SVG-based bubble shape definitions.
 * Each shape provides a path generator that returns an SVG path string
 * for the bubble outline, given width and height.
 */

const roundedRectPath = (w, h, r) => {
  const cr = Math.min(r, w / 2, h / 2);
  return `M ${cr},0 L ${w - cr},0 Q ${w},0 ${w},${cr} L ${w},${h - cr} Q ${w},${h} ${w - cr},${h} L ${cr},${h} Q 0,${h} 0,${h - cr} L 0,${cr} Q 0,0 ${cr},0 Z`;
};

const jaggedStarburstPath = (w, h) => {
  const cx = w / 2;
  const cy = h / 2;
  const points = 16;
  const outerRx = w / 2;
  const outerRy = h / 2;
  const innerRx = outerRx * 0.75;
  const innerRy = outerRy * 0.75;
  let d = "";
  for (let i = 0; i < points; i++) {
    const angle = (Math.PI * 2 * i) / points - Math.PI / 2;
    const midAngle = angle + Math.PI / points;
    const ox = cx + outerRx * Math.cos(angle);
    const oy = cy + outerRy * Math.sin(angle);
    const ix = cx + innerRx * Math.cos(midAngle);
    const iy = cy + innerRy * Math.sin(midAngle);
    d += i === 0 ? `M ${ox},${oy}` : ` L ${ox},${oy}`;
    d += ` L ${ix},${iy}`;
  }
  d += " Z";
  return d;
};

const thoughtCloudPath = (w, h) => {
  // Scalloped edge using overlapping arcs
  const cx = w / 2;
  const cy = h / 2;
  const rx = w / 2 - 4;
  const ry = h / 2 - 4;
  const bumps = 12;
  let d = "";
  for (let i = 0; i < bumps; i++) {
    const a1 = (Math.PI * 2 * i) / bumps - Math.PI / 2;
    const a2 = (Math.PI * 2 * (i + 1)) / bumps - Math.PI / 2;
    const x1 = cx + rx * Math.cos(a1);
    const y1 = cy + ry * Math.sin(a1);
    const x2 = cx + rx * Math.cos(a2);
    const y2 = cy + ry * Math.sin(a2);
    const midA = (a1 + a2) / 2;
    const bumpR = Math.max(rx, ry) * 0.15;
    const cpx = cx + (rx + bumpR) * Math.cos(midA);
    const cpy = cy + (ry + bumpR) * Math.sin(midA);
    if (i === 0) d += `M ${x1},${y1}`;
    d += ` Q ${cpx},${cpy} ${x2},${y2}`;
  }
  d += " Z";
  return d;
};

const wavyPath = (w, h) => {
  const r = 8;
  const segments = Math.max(4, Math.round((w + h) / 30));
  // Top edge
  let d = `M ${r},0`;
  const topSegs = Math.max(2, Math.round(w / 30));
  for (let i = 0; i < topSegs; i++) {
    const x1 = r + ((w - 2 * r) * (i + 0.5)) / topSegs;
    const x2 = r + ((w - 2 * r) * (i + 1)) / topSegs;
    d += ` Q ${x1},${i % 2 === 0 ? -5 : 5} ${x2},0`;
  }
  // Right edge
  const rightSegs = Math.max(2, Math.round(h / 30));
  for (let i = 0; i < rightSegs; i++) {
    const y1 = r + ((h - 2 * r) * (i + 0.5)) / rightSegs;
    const y2 = r + ((h - 2 * r) * (i + 1)) / rightSegs;
    d += ` Q ${i % 2 === 0 ? w + 5 : w - 5},${y1} ${w},${y2}`;
  }
  // Bottom edge
  const botSegs = topSegs;
  for (let i = botSegs - 1; i >= 0; i--) {
    const x1 = r + ((w - 2 * r) * (i + 0.5)) / botSegs;
    const x2 = r + ((w - 2 * r) * i) / botSegs;
    d += ` Q ${x1},${i % 2 === 0 ? h + 5 : h - 5} ${x2},${h}`;
  }
  // Left edge
  const leftSegs = rightSegs;
  for (let i = leftSegs - 1; i >= 0; i--) {
    const y1 = r + ((h - 2 * r) * (i + 0.5)) / leftSegs;
    const y2 = r + ((h - 2 * r) * i) / leftSegs;
    d += ` Q ${i % 2 === 0 ? -5 : 5},${y1} 0,${y2}`;
  }
  d += " Z";
  return d;
};

const jaggedRectPath = (w, h) => {
  const step = 8;
  const depth = 3;
  let d = `M 0,0`;
  // Top
  for (let x = step; x <= w; x += step) {
    const y = (x / step) % 2 === 0 ? 0 : -depth;
    d += ` L ${Math.min(x, w)},${y}`;
  }
  d += ` L ${w},0`;
  // Right
  for (let y = step; y <= h; y += step) {
    const x = (y / step) % 2 === 0 ? w : w + depth;
    d += ` L ${x},${Math.min(y, h)}`;
  }
  d += ` L ${w},${h}`;
  // Bottom
  for (let x = w - step; x >= 0; x -= step) {
    const y = ((w - x) / step) % 2 === 0 ? h : h + depth;
    d += ` L ${Math.max(x, 0)},${y}`;
  }
  d += ` L 0,${h}`;
  // Left
  for (let y = h - step; y >= 0; y -= step) {
    const x = ((h - y) / step) % 2 === 0 ? 0 : -depth;
    d += ` L ${x},${Math.max(y, 0)}`;
  }
  d += " Z";
  return d;
};

export const BUBBLE_SHAPES = [
  {
    key: "rounded",
    label: "Rounded",
    getPath: (w, h) => roundedRectPath(w, h, 10),
    tailType: "pointed",
    cssRadius: 10,
  },
  {
    key: "square",
    label: "Square",
    getPath: (w, h) => roundedRectPath(w, h, 0),
    tailType: "pointed",
    cssRadius: 0,
  },
  {
    key: "cloud",
    label: "Cloud",
    getPath: (w, h) => thoughtCloudPath(w, h),
    tailType: "thought",
    cssRadius: 24,
  },
  {
    key: "oval",
    label: "Oval",
    getPath: (w, h) => {
      const rx = w / 2;
      const ry = h / 2;
      return `M ${rx},0 A ${rx},${ry} 0 1,1 ${rx},${h} A ${rx},${ry} 0 1,1 ${rx},0 Z`;
    },
    tailType: "pointed",
    cssRadius: "50%",
  },
  {
    key: "starburst",
    label: "Starburst",
    getPath: jaggedStarburstPath,
    tailType: "pointed",
    cssRadius: 0,
  },
  {
    key: "dashed",
    label: "Dashed (Whisper)",
    getPath: (w, h) => roundedRectPath(w, h, 10),
    tailType: "pointed",
    cssRadius: 10,
    strokeDasharray: "6,4",
  },
  {
    key: "narration",
    label: "Narration Box",
    getPath: (w, h) => roundedRectPath(w, h, 2),
    tailType: "none",
    cssRadius: 2,
    fill: "#fffde7",
    noTail: true,
  },
  {
    key: "jagged",
    label: "Jagged (Radio)",
    getPath: jaggedRectPath,
    tailType: "pointed",
    cssRadius: 0,
  },
  {
    key: "wavy",
    label: "Wavy (Telepathy)",
    getPath: wavyPath,
    tailType: "pointed",
    cssRadius: 0,
  },
  {
    key: "double",
    label: "Double Outline",
    getPath: (w, h) => roundedRectPath(w, h, 10),
    tailType: "pointed",
    cssRadius: 10,
    doubleOutline: true,
  },
];

export const findBubbleShape = (key) =>
  BUBBLE_SHAPES.find((s) => s.key === key) || BUBBLE_SHAPES[0];
